import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminDeals from "./AdminDeals";
import AdminMunimjiErrors from "./AdminMunimjiErrors";
import { Users, AlertOctagon, Store, Link2, Globe, Bot, Receipt, Shield, LogOut, ArrowLeft, Trash2, Pencil, Plus, Stethoscope, Check, Copy, AlertTriangle } from "lucide-react";
import { AdminClaims, AdminUsersSummary, AdminHomeChecks, AdminLabBookings, AdminImageFixes } from "./AdminIBloom";
import AdminQCOrders from "./AdminQCOrders";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [shops, setShops] = useState([]);
  const [links, setLinks] = useState([]);
  const [sites, setSites] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [searchLogs, setSearchLogs] = useState({ total: 0, top: [], recent: [] });
  const [prompt, setPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data));
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data));
  const loadInvoices = () => api.get("/admin/invoices").then((r) => setInvoices(r.data));
  const loadAlerts = () => api.get("/admin/ai-alerts").then((r) => setAlerts(r.data));
  const loadShops = () => api.get("/admin/shops").then((r) => setShops(r.data));
  const loadLinks = () => api.get("/admin/affiliate-links").then((r) => setLinks(r.data));
  const loadSites = () => api.get("/admin/netbazzar-sites").then((r) => setSites(r.data));
  const loadHandoffs = () => api.get("/admin/doctor-handoffs").then((r) => setHandoffs(r.data));
  const loadSearchLogs = () => api.get("/admin/search-logs").then((r) => setSearchLogs(r.data));
  const loadPrompt = () => api.get("/admin/ai-prompt").then((r) => setPrompt(r.data.system_prompt || ""));

  useEffect(() => {
    loadStats(); loadUsers(); loadInvoices(); loadAlerts(); loadShops(); loadLinks(); loadSites(); loadHandoffs(); loadSearchLogs(); loadPrompt();
  }, []);

  const decide = async (id, approve, reason = "") => {
    await api.post(`/admin/invoices/${id}/decide`, { approve, reason });
    toast.success(approve ? "Approved" : "Rejected");
    loadInvoices(); loadStats();
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try { await api.put("/admin/ai-prompt", { system_prompt: prompt }); toast.success("Mapbazar AI prompt updated"); }
    catch { toast.error("Failed to save"); }
    finally { setSavingPrompt(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/home")} className="rounded-xl" data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> App
            </Button>
            <div className="flex items-center gap-2">
              <img src="/icon-192.png" alt="Mapbazar" className="w-9 h-9 rounded-xl" />
              <div>
                <div className="font-display font-bold text-slate-900">Mapbazar Admin</div>
                <div className="text-xs text-slate-500">{user?.email}</div>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }} className="rounded-xl" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-main">
        <section className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard testid="admin-stat-users" label="Users" value={stats.users || 0} icon={<Users className="w-5 h-5" />} />
          <StatCard testid="admin-stat-pending-invoices" label="Pending invoices" value={stats.invoices_pending || 0} icon={<Receipt className="w-5 h-5" />} accent="rewards" />
          <StatCard testid="admin-stat-ai-alerts" label="AI alerts" value={stats.ai_alerts || 0} icon={<AlertOctagon className="w-5 h-5" />} />
          <StatCard testid="admin-stat-shops" label="Shops" value={stats.shops || 0} icon={<Store className="w-5 h-5" />} />
          <StatCard testid="admin-stat-handoffs" label="Doctor handoffs" value={handoffs.length} icon={<Stethoscope className="w-5 h-5" />} />
        </section>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-xl bg-white border border-slate-100 p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="searches" data-testid="admin-tab-searches">🔎 Searches</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">🛒 Orders</TabsTrigger>
            <TabsTrigger value="image-fixes" data-testid="admin-tab-image-fixes">📸 Image fixes</TabsTrigger>
            <TabsTrigger value="claims" data-testid="admin-tab-claims">Claims</TabsTrigger>
            <TabsTrigger value="ibloom-users" data-testid="admin-tab-ibloom-users">All Users</TabsTrigger>
            <TabsTrigger value="home-checks" data-testid="admin-tab-home-checks">Home Checks</TabsTrigger>
            <TabsTrigger value="lab-bookings" data-testid="admin-tab-lab-bookings">Lab Bookings</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users (raw)</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="admin-tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="admin-tab-alerts">AI Alerts</TabsTrigger>
            <TabsTrigger value="handoffs" data-testid="admin-tab-handoffs">Handoffs</TabsTrigger>
            <TabsTrigger value="ai-prompt" data-testid="admin-tab-ai-prompt">Mapbazar AI</TabsTrigger>
            <TabsTrigger value="shops" data-testid="admin-tab-shops">Shops</TabsTrigger>
            <TabsTrigger value="affiliate" data-testid="admin-tab-affiliate">Affiliate</TabsTrigger>
            <TabsTrigger value="netbazzar" data-testid="admin-tab-netbazzar">Mapbazar</TabsTrigger>
            <TabsTrigger value="deals" data-testid="admin-tab-deals">🔥 Deals</TabsTrigger>
            <TabsTrigger value="munimji-errors" data-testid="admin-tab-munimji-errors">⚠️ ShopKeeper AI Errors</TabsTrigger>
            <TabsTrigger value="rewards" data-testid="admin-tab-rewards">Rewards</TabsTrigger>
          </TabsList>

          <TabsContent value="searches" className="mt-4">
            <div className="space-y-4" data-testid="admin-searches">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">What people are searching</h3>
                  <p className="text-sm text-slate-500">{searchLogs.total} total searches · <span className="text-red-600 font-semibold">{searchLogs.unfulfilled_total || 0} unfulfilled</span> · default area Ahmedabad</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={loadSearchLogs} data-testid="admin-searches-refresh">Refresh</Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="rounded-2xl border-slate-100"><CardContent className="p-4">
                  <div className="font-semibold text-slate-800 mb-3">🔥 Top searches</div>
                  {(!searchLogs.top || searchLogs.top.length === 0) && <div className="text-sm text-slate-500">No searches logged yet.</div>}
                  <div className="space-y-2">
                    {(searchLogs.top || []).map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-sm gap-2" data-testid={`admin-top-search-${i}`}>
                        <span className="text-slate-700 truncate">{i + 1}. {t.query}</span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          {t.whatsapp_requests > 0 && (
                            <span className="text-xs font-bold bg-red-50 text-red-700 px-2 py-0.5 rounded-full" title="Shoppers asked us to source this on WhatsApp — unfulfilled demand">
                              🟢 {t.whatsapp_requests} wanted
                            </span>
                          )}
                          {t.avg_results != null && (
                            <span className={`text-xs ${t.avg_results === 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                              {t.avg_results === 0 ? "no results" : `~${t.avg_results} found`}
                            </span>
                          )}
                          <span className="text-xs font-bold bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">{t.count}×</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent></Card>
                <Card className="rounded-2xl border-slate-100"><CardContent className="p-4">
                  <div className="font-semibold text-slate-800 mb-3">🕑 Recent searches</div>
                  {(!searchLogs.recent || searchLogs.recent.length === 0) && <div className="text-sm text-slate-500">No searches logged yet.</div>}
                  <div className="space-y-2 max-h-[440px] overflow-y-auto">
                    {(searchLogs.recent || []).map((r) => (
                      <div key={r.id} className="text-sm border-b border-slate-50 pb-1.5" data-testid={`admin-recent-search-${r.id}`}>
                        <div className="text-slate-700 flex items-center gap-1.5">
                          “{r.query}”
                          {r.whatsapp_requested && <span className="text-[10px] font-bold bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full">🟢 WhatsApp</span>}
                          {!r.whatsapp_requested && r.unfulfilled && <span className="text-[10px] font-bold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">unfulfilled</span>}
                        </div>
                        <div className="text-xs text-slate-400">
                          {r.location || "—"}
                          {r.results_count != null ? ` · ${r.results_count} found` : ""}
                          {r.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent></Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="deals" className="mt-4">
            <AdminDeals />
          </TabsContent>

          <TabsContent value="munimji-errors" className="mt-4">
            <AdminMunimjiErrors />
          </TabsContent>

          <TabsContent value="overview" className="mt-4">
            <Card className="rounded-2xl border-slate-100"><CardContent className="p-6">
              <h3 className="font-display text-xl font-semibold text-slate-900">Welcome, Admin</h3>
              <p className="text-sm text-slate-500 mt-1">Manage claims, users, Home Checks, Lab Bookings and the Mapbazar AI.</p>
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                <OverviewLink onClick={() => setTab("claims")} label="Verify pending credit claims" count={null} icon={<Receipt className="w-4 h-4" />} />
                <OverviewLink onClick={() => setTab("home-checks")} label="Home Check schedule" count={null} icon={<Stethoscope className="w-4 h-4" />} />
                <OverviewLink onClick={() => setTab("lab-bookings")} label="Lab Bookings status" count={null} icon={<Stethoscope className="w-4 h-4" />} />
              </div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="claims" className="mt-4">
            <AdminClaims />
          </TabsContent>

          <TabsContent value="orders" className="mt-4">
            <AdminQCOrders />
          </TabsContent>

          <TabsContent value="image-fixes" className="mt-4">
            <AdminImageFixes />
          </TabsContent>

          <TabsContent value="ibloom-users" className="mt-4">
            <AdminUsersSummary />
          </TabsContent>

          <TabsContent value="home-checks" className="mt-4">
            <AdminHomeChecks />
          </TabsContent>

          <TabsContent value="lab-bookings" className="mt-4">
            <AdminLabBookings />
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <Card className="rounded-2xl border-slate-100"><CardContent className="p-0">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
                  <th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Role</th><th className="p-3 text-left">Auth</th><th className="p-3 text-left">Joined</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-${u.id}`}>
                      <td className="p-3 font-medium text-slate-900">{u.name}</td>
                      <td className="p-3 text-slate-600">{u.email}</td>
                      <td className="p-3 capitalize">{u.role}</td>
                      <td className="p-3 capitalize text-slate-500">{u.auth_method}</td>
                      <td className="p-3 text-slate-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="invoices" className="mt-4">
            <div className="space-y-2" data-testid="admin-invoices-list">
              {invoices.length === 0 && <div className="text-sm text-slate-500">No invoices.</div>}
              {invoices.map((inv) => (
                <Card key={inv.id} className="rounded-2xl border-slate-100" data-testid={`admin-invoice-${inv.id}`}>
                  <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900">{inv.merchant} · ₹{inv.amount}</div>
                      <div className="text-xs text-slate-500">{inv.date} · {inv.category} · {inv.user_id.slice(0, 8)}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-medium ${inv.status === "pending" ? "bg-amber-50 text-amber-700" : inv.status === "approved" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{inv.status}</span>
                    <div className="font-semibold text-[#F26440]">{inv.points_awarded || inv.points_pending} pts</div>
                    {inv.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" onClick={() => decide(inv.id, true)} data-testid={`admin-approve-${inv.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-100 hover:bg-red-50" onClick={() => decide(inv.id, false, "Not eligible")} data-testid={`admin-reject-${inv.id}`}>Reject</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="alerts" className="mt-4">
            <div className="space-y-2" data-testid="admin-alerts-list">
              {alerts.length === 0 && <div className="text-sm text-slate-500">No alerts. Your users are safe.</div>}
              {alerts.map((a) => (
                <Card key={a.id} className="rounded-2xl border-red-100" data-testid={`admin-alert-${a.id}`}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"><AlertOctagon className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900">{a.user_email}</div>
                      <div className="text-sm text-slate-600">“{a.message}”</div>
                      <div className="text-xs text-slate-400 mt-1">Severity: {a.severity} · {new Date(a.created_at).toLocaleString()}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="handoffs" className="mt-4">
            <AdminHandoffs handoffs={handoffs} />
          </TabsContent>

          <TabsContent value="ai-prompt" className="mt-4">
            <Card className="rounded-2xl border-slate-100"><CardContent className="p-5 space-y-3">
              <h3 className="font-display text-lg font-semibold text-slate-900">Mapbazar AI system prompt</h3>
              <p className="text-sm text-slate-500">Customize the safety behavior, tone and medical guardrails. This applies to every chat.</p>
              <Textarea rows={14} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="rounded-xl font-mono text-sm" data-testid="admin-ai-prompt-textarea" />
              <div className="flex gap-2">
                <Button onClick={savePrompt} disabled={savingPrompt} className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" data-testid="admin-ai-prompt-save">Save</Button>
                <Button variant="outline" onClick={loadPrompt} className="rounded-xl">Reset</Button>
              </div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="shops" className="mt-4"><AdminShops shops={shops} reload={loadShops} /></TabsContent>
          <TabsContent value="affiliate" className="mt-4"><AdminAffiliate links={links} reload={loadLinks} /></TabsContent>
          <TabsContent value="netbazzar" className="mt-4"><AdminMapbazar sites={sites} reload={loadSites} /></TabsContent>
          <TabsContent value="rewards" className="mt-4"><AdminVitalityRewards /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const StatCard = ({ label, value, icon, accent, testid }) => (
  <Card className="rounded-2xl border-slate-100" data-testid={testid}>
    <CardContent className="p-4">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent === "rewards" ? "bg-orange-50 text-[#F26440]" : "bg-teal-50 text-[#0F766E]"}`}>{icon}</div>
      <div className="font-display text-2xl font-bold mt-2 text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </CardContent>
  </Card>
);

const OverviewLink = ({ onClick, label, count, icon }) => (
  <button onClick={onClick} className="text-left p-4 bg-white rounded-2xl border border-slate-100 hover:border-slate-200">
    <div className="w-8 h-8 rounded-lg bg-teal-50 text-[#0F766E] flex items-center justify-center">{icon}</div>
    <div className="mt-2 text-sm font-medium text-slate-800">{label}</div>
    {count != null && <div className="text-xs text-slate-500 mt-0.5">{count} item(s)</div>}
  </button>
);

// ---------- CRUD widgets ----------
const emptyShop = { name: "", address: "", lat: 12.97, lng: 77.59, category: "pharmacy", phone: "", image: "" };
const emptyLink = { title: "", url: "", image: "", category: "wellness" };
const emptySite = { name: "", url: "", description: "", image: "" };

function AdminShops({ shops, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyShop);
  const [editingId, setEditingId] = useState(null);

  const save = async () => {
    try {
      if (editingId) { await api.put(`/admin/shops/${editingId}`, form); }
      else { await api.post("/admin/shops", form); }
      toast.success("Saved"); setOpen(false); setForm(emptyShop); setEditingId(null); reload();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete shop?")) return; await api.delete(`/admin/shops/${id}`); reload(); toast.success("Deleted"); };
  const approve = async (id) => { await api.post(`/admin/shops/${id}/approve`); toast.success("Approved"); reload(); };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" onClick={() => { setForm(emptyShop); setEditingId(null); setOpen(true); }} data-testid="admin-shop-add-btn"><Plus className="w-4 h-4 mr-1" /> Add shop</Button>
      </div>
      <div className="space-y-2" data-testid="admin-shops-list">
        {shops.map((s) => (
          <Card key={s.id} className="rounded-2xl border-slate-100" data-testid={`admin-shop-${s.id}`}>
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              {s.image && <img src={s.image} className="w-12 h-12 rounded-xl object-cover" alt="" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500 truncate">{s.address} · {s.category}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${s.approved ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{s.approved ? "Approved" : "Pending"}</span>
              {!s.approved && (
                <Button size="sm" className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" onClick={() => approve(s.id)} data-testid={`admin-shop-approve-${s.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              )}
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => { setForm({ name: s.name, address: s.address, lat: s.lat, lng: s.lng, category: s.category, phone: s.phone || "", image: s.image || "" }); setEditingId(s.id); setOpen(true); }} data-testid={`admin-shop-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-100" onClick={() => del(s.id)} data-testid={`admin-shop-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit" : "Add"} shop</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Inp label="Name" val={form.name} on={(v) => setForm({ ...form, name: v })} />
            <Inp label="Address" val={form.address} on={(v) => setForm({ ...form, address: v })} />
            <div className="grid grid-cols-2 gap-2">
              <Inp label="Lat" val={form.lat} on={(v) => setForm({ ...form, lat: parseFloat(v) || 0 })} />
              <Inp label="Lng" val={form.lng} on={(v) => setForm({ ...form, lng: parseFloat(v) || 0 })} />
            </div>
            <Inp label="Category" val={form.category} on={(v) => setForm({ ...form, category: v })} />
            <Inp label="Phone" val={form.phone} on={(v) => setForm({ ...form, phone: v })} />
            <Inp label="Image URL" val={form.image} on={(v) => setForm({ ...form, image: v })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" data-testid="admin-shop-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdminAffiliate({ links, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLink);
  const [editingId, setEditingId] = useState(null);
  const save = async () => {
    try { if (editingId) await api.put(`/admin/affiliate-links/${editingId}`, form); else await api.post("/admin/affiliate-links", form); toast.success("Saved"); setOpen(false); setForm(emptyLink); setEditingId(null); reload(); } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete link?")) return; await api.delete(`/admin/affiliate-links/${id}`); reload(); };
  return (
    <>
      <div className="flex justify-end mb-3"><Button className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" onClick={() => { setForm(emptyLink); setEditingId(null); setOpen(true); }} data-testid="admin-affiliate-add-btn"><Plus className="w-4 h-4 mr-1" /> Add link</Button></div>
      <div className="grid sm:grid-cols-2 gap-2" data-testid="admin-affiliate-list">
        {links.map((l) => (
          <Card key={l.id} className="rounded-2xl border-slate-100" data-testid={`admin-affiliate-${l.id}`}>
            <CardContent className="p-4 flex items-center gap-3">
              {l.image && <img src={l.image} className="w-12 h-12 rounded-xl object-cover" alt="" />}
              <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 truncate">{l.title}</div><div className="text-xs text-slate-500 truncate">{l.url}</div></div>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => { setForm({ title: l.title, url: l.url, image: l.image || "", category: l.category }); setEditingId(l.id); setOpen(true); }} data-testid={`admin-affiliate-edit-${l.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-100" onClick={() => del(l.id)} data-testid={`admin-affiliate-delete-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit" : "Add"} affiliate link</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Inp label="Title" val={form.title} on={(v) => setForm({ ...form, title: v })} />
            <Inp label="URL" val={form.url} on={(v) => setForm({ ...form, url: v })} />
            <Inp label="Image URL" val={form.image} on={(v) => setForm({ ...form, image: v })} />
            <Inp label="Category" val={form.category} on={(v) => setForm({ ...form, category: v })} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button><Button onClick={save} className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" data-testid="admin-affiliate-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdminMapbazar({ sites, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySite);
  const [editingId, setEditingId] = useState(null);
  const save = async () => {
    try { if (editingId) await api.put(`/admin/netbazzar-sites/${editingId}`, form); else await api.post("/admin/netbazzar-sites", form); toast.success("Saved"); setOpen(false); setForm(emptySite); setEditingId(null); reload(); } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete site?")) return; await api.delete(`/admin/netbazzar-sites/${id}`); reload(); };
  return (
    <>
      <div className="flex justify-end mb-3"><Button className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" onClick={() => { setForm(emptySite); setEditingId(null); setOpen(true); }} data-testid="admin-netbazzar-add-btn"><Plus className="w-4 h-4 mr-1" /> Add site</Button></div>
      <div className="grid sm:grid-cols-2 gap-2" data-testid="admin-netbazzar-list">
        {sites.map((s) => (
          <Card key={s.id} className="rounded-2xl border-slate-100" data-testid={`admin-netbazzar-${s.id}`}>
            <CardContent className="p-4 flex items-center gap-3">
              {s.image && <img src={s.image} className="w-12 h-12 rounded-xl object-cover" alt="" />}
              <div className="flex-1 min-w-0"><div className="font-medium text-slate-900 truncate">{s.name}</div><div className="text-xs text-slate-500 truncate">{s.url}</div><div className="text-xs text-slate-400 truncate">{s.description}</div></div>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => { setForm({ name: s.name, url: s.url, description: s.description, image: s.image || "" }); setEditingId(s.id); setOpen(true); }} data-testid={`admin-netbazzar-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-100" onClick={() => del(s.id)} data-testid={`admin-netbazzar-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit" : "Add"} Mapbazar site</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Inp label="Name" val={form.name} on={(v) => setForm({ ...form, name: v })} />
            <Inp label="URL" val={form.url} on={(v) => setForm({ ...form, url: v })} />
            <Inp label="Description" val={form.description} on={(v) => setForm({ ...form, description: v })} />
            <Inp label="Image URL" val={form.image} on={(v) => setForm({ ...form, image: v })} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button><Button onClick={save} className="rounded-xl bg-[#0F766E] hover:bg-[#115E59]" data-testid="admin-netbazzar-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Inp = ({ label, val, on }) => (
  <div className="space-y-1.5">
    <Label className="text-slate-700">{label}</Label>
    <Input value={val} onChange={(e) => on(e.target.value)} className="rounded-xl" data-testid={`admin-input-${label.toLowerCase().replace(/\s+/g,'-')}`} />
  </div>
);

function AdminHandoffs({ handoffs }) {
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("SBAR copied to clipboard"); };
  if (!handoffs.length) {
    return <Card className="rounded-2xl border-slate-100"><CardContent className="p-6 text-sm text-slate-500" data-testid="admin-handoffs-empty">No doctor handoffs yet. The Mapbazar will auto-generate SBAR reports here after triage conversations.</CardContent></Card>;
  }
  return (
    <div className="space-y-3" data-testid="admin-handoffs-list">
      {handoffs.map((h) => (
        <Card key={h.id} className={`rounded-2xl ${h.urgent ? "border-red-200 bg-red-50/30" : "border-slate-100"}`} data-testid={`admin-handoff-${h.id}`}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${h.urgent ? "bg-red-100 text-red-600" : "bg-teal-50 text-[#0F766E]"}`}>
                {h.urgent ? <AlertTriangle className="w-4 h-4" /> : <Stethoscope className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{h.user_email || "unknown user"}</div>
                <div className="text-xs text-slate-500">Session {String(h.session_id || "").slice(0, 8)} · {new Date(h.created_at).toLocaleString()}</div>
              </div>
              {h.urgent && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" data-testid={`admin-handoff-urgent-${h.id}`}>URGENT</span>}
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => copy(h.sbar)} data-testid={`admin-handoff-copy-${h.id}`}><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
            </div>
            <pre className="text-xs bg-white rounded-xl p-3 border border-slate-100 whitespace-pre-wrap text-slate-700 font-mono leading-relaxed" data-testid={`admin-handoff-sbar-${h.id}`}>{h.sbar}</pre>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


function AdminVitalityRewards() {
  const [rewards, setRewards] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/admin/vitality-rewards${statusFilter ? `?status=${statusFilter}` : ""}`);
      setRewards(r.data || []);
    } catch (e) {
      toast.error("Couldn't load rewards");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/admin/vitality-rewards/${id}/${action}`, { note: "" });
      toast.success(action === "approve" ? "Reward approved" : "Reward rejected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${action}`);
    }
    setBusyId(null);
  };

  const counts = rewards.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="admin-vitality-rewards">
      <Card className="rounded-2xl border-slate-100 mb-4"><CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <Shield className="w-5 h-5 text-amber-600 mt-1" />
          <div>
            <div className="font-semibold text-slate-900">Checkup reward approvals</div>
            <div className="text-xs text-slate-500 mt-1">Vitality rewards are <strong>never auto-unlocked</strong>. Each one stays pending until manually approved here, after which the user can claim the Amazon affiliate link from their Profile → My Rewards.</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { k: "pending", l: `Pending (${counts.pending || 0})` },
            { k: "approved", l: `Approved (${counts.approved || 0})` },
            { k: "rejected", l: "Rejected" },
            { k: "", l: "All" },
          ].map((f) => (
            <button key={f.k || "all"} data-testid={`admin-rewards-filter-${f.k || "all"}`}
              onClick={() => setStatusFilter(f.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${statusFilter === f.k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
              {f.l}
            </button>
          ))}
        </div>
      </CardContent></Card>

      {rewards.length === 0 && (
        <div className="text-center py-12 text-slate-400 text-sm">No rewards in this bucket.</div>
      )}

      <div className="space-y-3">
        {rewards.map((r) => (
          <Card key={r.id} className="rounded-2xl border-slate-100" data-testid={`admin-reward-${r.id}`}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="text-2xl">🎁</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">
                    {r.reward_title} <span className="text-slate-400 font-normal">· {r.checkup_title}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    For: <strong>{r.member_name || "—"}</strong> · User: {r.user_id?.slice(0, 8)}… · {(r.created_at || "").slice(0, 19).replace("T", " ")} UTC
                  </div>
                  <div className="text-xs text-amber-700 mt-1 break-all">
                    Affiliate URL: <span className="font-mono">{r.reward_url}</span>
                  </div>
                  {r.admin_note && <div className="text-xs text-slate-500 mt-2 italic">Note: {r.admin_note}</div>}
                  <div className="mt-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      r.status === "pending" ? "bg-amber-100 text-amber-800" :
                      r.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                      "bg-rose-100 text-rose-800"
                    }`}>{(r.status || "").toUpperCase()}</span>
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button data-testid={`admin-reward-approve-${r.id}`} size="sm" onClick={() => decide(r.id, "approve")} disabled={busyId === r.id} className="bg-emerald-600 hover:bg-emerald-700">
                      <Check className="w-4 h-4 mr-1" />Approve
                    </Button>
                    <Button data-testid={`admin-reward-reject-${r.id}`} size="sm" variant="outline" onClick={() => decide(r.id, "reject")} disabled={busyId === r.id}>
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
